import React, { useState } from "react";
import {
  Link,
  CommonAuthFields,
  CommonAuthButton,
  SliderWrapper,
  EmailInput,
} from "../index";
import styles from "./NewSignup.module.css";
import Logo from "../../../assets/img/skillgigs.png";
import { NEW_ONBOARDING_ROUTES } from "../constants/routes.constants";
import { validateEmail } from "../utils/passwordUtils";
import { useUTMToLocalStorage } from "../../../shared/hooks";
import ReactGA from "react-ga4";
import { APP_NAME, EVENTS, PAGE, PAGE_TYPE } from "../constants/userTrackingConstants";
import { userClicked } from "../../../redux/actions";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { GoAlert } from "react-icons/go";
import GreenCheck from "../../../assets/icons/typcn_tick.svg";
import { alertNotification } from "../../../components/TopNotification/alertNotification";
import { passwordlessStartApi } from "../../../API/API";

export const NewSignup = () => {
  const uuid = useSelector((state) => state.userTrackingReducer.uuid);
  const dispatch = useDispatch();
  const history = useHistory();
  useUTMToLocalStorage();

  const [email, setEmail] = useState("");
  const [emailFocused, setEmailFocused] = useState(false);
  const [greenCheck, setGreenCheck] = useState(false);
  const [showCheckIconEmail, setShowCheckIconEmail] = useState(false);
  const [workEmailError, setWorkEmailError] = useState("");
  const [loading, setLoading] = useState(false);

  const validateEmailLive = (e) => {
    const value = e.target.value.toLowerCase();
    setEmail(value);

    if (value.length > 0) {
      setShowCheckIconEmail(true);
    } else {
      setShowCheckIconEmail(false);
    }

    setWorkEmailError("");
    setGreenCheck(validateEmail(value));
  };

  const recordClick = (event, label = "Signup Button") => {
    ReactGA.event({
      category: "User Interaction",
      action: `${PAGE.STATIC} ${event}`,
      label,
    });

    dispatch(
      userClicked({
        uuid,
        event,
        pageType: PAGE_TYPE.SIGNUP,
        page: PAGE.STATIC,
        appName: APP_NAME.EMPLOYER,
        url: window?.location?.pathname,
      }),
    );
  };

  const handleSocialClick = (event) => {
    recordClick(event, `${event} Signup Button`);
  };

  const isSignupDisabled = loading || !email || !validateEmail(email);

  const handleContinue = async () => {
    if (isSignupDisabled) return;
    const normalizedEmail = email.toLowerCase();

    setLoading(true);
    try {
      recordClick(EVENTS.SIGN_UP_BUTTON_CLICK);

      const response = await passwordlessStartApi({
        email: normalizedEmail,
        purpose: "signup",
      });
      const data = response?.data || {};

      localStorage.setItem("Email", normalizedEmail);
      history.push("/verify-email", {
        email: normalizedEmail,
        fromSignup: true,
        challengeSession: data.session,
      });
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Unable to start signup. Please try again.";
      setWorkEmailError(message);
      alertNotification("error", message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleContinue();
  };

  return (
    <SliderWrapper>
      <div className={styles.formCard}>
        <div className={styles.brandWrap}>
          <img src={Logo} alt="SkillGigs" className={styles.brandLogo} />
        </div>

        <h2 className={styles.title}>Create your free account</h2>
        <p className={styles.subtitle}>100% free. No credit card needed.</p>

        <form className={styles.form} onSubmit={handleSubmit}>
          <CommonAuthFields
            handleSocialClick={handleSocialClick}
            isSignup={true}
          />

          <div
            className={
              workEmailError
                ? styles.invalidInput
                : !emailFocused
                  ? greenCheck
                    ? styles.validInput
                    : showCheckIconEmail
                      ? styles.invalidInput
                      : ""
                  : ""
            }
          >
            <EmailInput
              autoFocus
              required
              label="Email Address"
              value={email}
              handleChange={validateEmailLive}
              onFocus={() => setEmailFocused(true)}
              onBlur={() => setEmailFocused(false)}
              placeholder="Enter the email address"
              suffix={
                greenCheck ? (
                  <img src={GreenCheck} alt="valid" />
                ) : showCheckIconEmail && !emailFocused ? (
                  <GoAlert style={{ color: "#db2828" }} />
                ) : null
              }
            />
          </div>

          {showCheckIconEmail &&
            !greenCheck &&
            !emailFocused &&
            email?.length > 0 && (
              <p className={styles.errorText}>
                Looks like this email is incomplete
              </p>
            )}

          {workEmailError && (
            <p className={styles.errorText}>{workEmailError}</p>
          )}

          <CommonAuthButton
            type="submit"
            className={styles.submitBtn}
            disabled={isSignupDisabled}
            loading={loading}
            label={"Verify email"}
          />

          <p className={styles.privacyText}>
            We may contact you with relevant content, products, and services. You can unsubscribe at any time. Learn more in our{" "}
            <a
              href="https://skillgigs.com/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.privacyLink}
            >
              Privacy Policy
            </a>
          </p>

          <p className={styles.accountText}>
            Already have an account?{" "}
            <Link to={NEW_ONBOARDING_ROUTES.LOGIN} className={styles.accountLink}>
              Log in
            </Link>
          </p>
        </form>
      </div>
    </SliderWrapper>
  );
};
